import { createSelector } from 'reselect';
import type { HierarchyLevel } from '@jira/project-settings__issue-type-hierarchies/src/isSubtaskHierarchyLevel.tsx';
import {
	ISSUE_TYPE,
	SUBTASK_PARENT,
	PARENT,
} from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import type { IssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
import { issueKeySelector } from '../common/state/selectors/context-selector.tsx';
import { fieldSelector } from '../common/state/selectors/field-selector.tsx';
import { parentIssueEntitySelector } from '../common/state/selectors/issue-selector.tsx';

const createRelativeIssueUrl = (issueKey: string): string => `/browse/${issueKey}`;

export const createFullIssueUrl = (issueKey: string): string =>
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	`${window.location.protocol}//${window.location.host}${createRelativeIssueUrl(issueKey)}`;

const issueTypeFieldSelector = fieldSelector(ISSUE_TYPE);

const subtaskIssueFieldSelector = fieldSelector(SUBTASK_PARENT);

const portfolioParentFieldSelector = fieldSelector(PARENT);

export type ParentIssue = {
	key: string;
	summary: string;
	url: string;
	issueTypeName: string;
	issueTypeIconUrl: string;
};

type Issue = Omit<ParentIssue, 'url'>;

export type IssueType = {
	avatarId: number;
	description: string;
	hierarchyLevel: HierarchyLevel;
	iconUrl: string;
	id: IssueTypeId;
	name: string;
	self: string;
	subtask: boolean;
};

export const issueTypeSelector = createSelector(
	issueTypeFieldSelector,
	(issueType) => issueType && issueType.value,
);

export const issueUrlSelector = createSelector(issueKeySelector, createRelativeIssueUrl);

export const fullIssueUrlSelector = createSelector(issueKeySelector, createFullIssueUrl);

export const issueTypeNameSelector = createSelector(
	issueTypeSelector,
	(issueType) => issueType && issueType.name,
);

export const issueTypeHierarchyLevelSelector = createSelector(issueTypeSelector, (issueType) => {
	if (!issueType) {
		return null;
	}
	return issueType && issueType.hierarchyLevel;
});

export const issueTypeIdSelector = createSelector(
	issueTypeSelector,
	(issueType) => issueType && issueType.id,
);

export const issueTypeIconUrlSelector = createSelector(
	issueTypeSelector,
	(issueType) => issueType && issueType.iconUrl,
);

const transformParentIssue = (issue: Issue | null): ParentIssue | null =>
	issue
		? {
				summary: issue.summary,
				key: issue.key,
				url: createRelativeIssueUrl(issue.key),
				issueTypeName: issue.issueTypeName || '',
				issueTypeIconUrl: issue.issueTypeIconUrl || '',
			}
		: null;

// @ts-expect-error - TS7006 - Parameter 'issue' implicitly has an 'any' type.
const transformSubtaskParentIssue = (issue): ParentIssue | null =>
	issue
		? {
				summary: issue.fields.summary,
				key: issue.key,
				url: createRelativeIssueUrl(issue.key),
				issueTypeName: issue.issueTypeName || '',
				issueTypeIconUrl: issue.issueTypeIconUrl || '',
			}
		: null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformPortfolioParentIssue = (value: any): ParentIssue | null =>
	value && value.data
		? {
				summary: value.data.summary,
				key: value.data.key,
				url: createRelativeIssueUrl(value.data.key),
				issueTypeName: value.data.issueTypeName || '',
				issueTypeIconUrl: value.data.issueTypeIconUrl || '',
			}
		: null;

export const portfolioParentSelector = createSelector(
	portfolioParentFieldSelector,
	(portfolioParentIssue) =>
		transformPortfolioParentIssue(portfolioParentIssue && portfolioParentIssue.value),
);

const subtaskIssueParentSelector = createSelector(subtaskIssueFieldSelector, (subtaskIssue) =>
	transformSubtaskParentIssue(subtaskIssue && subtaskIssue.value),
);

const parentIssueSelector = createSelector(parentIssueEntitySelector, (parentIssue) =>
	transformParentIssue(parentIssue),
);

export const parentSelector = createSelector(
	parentIssueSelector,
	subtaskIssueParentSelector,
	portfolioParentSelector,
	(parentIssue, subtaskIssue, portfolioParentIssue) =>
		parentIssue || subtaskIssue || portfolioParentIssue,
);

export const getParentIssuesSelector = createSelector(parentIssueSelector, (parentIssue) =>
	[parentIssue].filter(Boolean),
);
